import { createAuthHeaders } from './auth/authHeaders.js';

function getApiBase() {
  const explicit = process.env.REACT_APP_SIGMUND_API_BASE;
  if (explicit && explicit.trim()) return explicit.trim();

  const prefix = process.env.REACT_APP_SIGMUND_API_PREFIX || '/sigmund/api';
  const devBackend = process.env.REACT_APP_BACKEND_URL_DEV || 'http://127.0.0.1:8000';
  const prodBackend = process.env.REACT_APP_BACKEND_URL_PROD || '';

  if (process.env.NODE_ENV === 'development') {
    return `${devBackend}${prefix}`;
  }

  if (prodBackend) {
    return `${prodBackend}${prefix}`;
  }

  return prefix;
}

const API_BASE = getApiBase();

export function getVoiceSocketUrl(companyUid, authToken = '') {
  const baseUrl = new URL(`${API_BASE}/voice/live`, window.location.origin);
  baseUrl.protocol = baseUrl.protocol === 'https:' ? 'wss:' : 'ws:';
  baseUrl.searchParams.set('company_uid', companyUid);
  // On same-origin deployments, backend can read auth_token cookie directly.
  // Keep query token only for cross-origin websocket targets.
  const isCrossOrigin = baseUrl.origin !== window.location.origin;
  if (isCrossOrigin && authToken && authToken.trim()) {
    baseUrl.searchParams.set('auth_token', authToken.trim());
  }
  return baseUrl.toString();
}

async function parseJson(response) {
  if (!response.ok) {
    const errText = await response.text();
    throw new Error(errText || 'Request failed');
  }
  return response.json();
}

export async function getHealth() {
  return parseJson(await fetch(`${API_BASE}/health`));
}

export async function getCompanySnapshot(companyUid, authToken = '') {
  return parseJson(
    await fetch(`${API_BASE}/company/${encodeURIComponent(companyUid)}/snapshot`, {
      headers: createAuthHeaders(authToken)
    })
  );
}

export async function sendMessage({
  companyUid,
  message,
  sessionId,
  mode,
  enableWebBrowsing = true,
  authToken = ''
}) {
  return parseJson(
    await fetch(`${API_BASE}/chat`, {
      method: 'POST',
      headers: createAuthHeaders(authToken, true),
      body: JSON.stringify({
        company_uid: companyUid,
        message,
        session_id: sessionId,
        mode,
        enable_web_browsing: enableWebBrowsing
      })
    })
  );
}

export async function streamMessage(
  {
    companyUid,
    message,
    sessionId,
    mode,
    enableWebBrowsing = true,
    authToken = ''
  },
  onEvent
) {
  const response = await fetch(`${API_BASE}/chat/stream`, {
    method: 'POST',
    headers: createAuthHeaders(authToken, true),
    body: JSON.stringify({
      company_uid: companyUid,
      message,
      session_id: sessionId,
      mode,
      enable_web_browsing: enableWebBrowsing
    })
  });

  if (!response.ok) {
    const errText = await response.text();
    throw new Error(errText || 'Streaming request failed');
  }

  if (!response.body) {
    throw new Error('Readable stream is not available in this browser.');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    let newlineIndex = buffer.indexOf('\n');
    while (newlineIndex !== -1) {
      const line = buffer.slice(0, newlineIndex).trim();
      buffer = buffer.slice(newlineIndex + 1);
      if (line) {
        try {
          const event = JSON.parse(line);
          if (onEvent) onEvent(event);
        } catch (_) {
          // Ignore malformed event lines.
        }
      }
      newlineIndex = buffer.indexOf('\n');
    }
  }

  const tail = buffer.trim();
  if (tail) {
    try {
      const event = JSON.parse(tail);
      if (onEvent) onEvent(event);
    } catch (_) {
      // no-op
    }
  }
}
